import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const ThemeContext = createContext(null);

// Helper to get system theme preference
const getSystemTheme = () => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export function ThemeProvider({ children }) {
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    try {
      return localStorage.getItem('themeMode') || 'light';
    } catch {
      return 'light';
    }
  });
  const [systemTheme, setSystemTheme] = useState(getSystemTheme());

  // Listen to system theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch theme mode from Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'brand_colors_preferences', 'default'),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const mode = data?.theme_mode || 'light';
          setThemeMode(mode);

          // Cache to localStorage
          try {
            localStorage.setItem('themeMode', mode);
          } catch (error) {
            console.error('Error caching theme mode:', error);
          }
        } else {
          // Use cached or default
          try {
            const cached = localStorage.getItem('themeMode');
            setThemeMode(cached || 'light');
          } catch (error) {
            setThemeMode('light');
          }
        }
      },
      (error) => {
        console.error('Error fetching theme mode:', error);
        // Use cached or default on error
        try {
          const cached = localStorage.getItem('themeMode');
          setThemeMode(cached || 'light');
        } catch (err) {
          setThemeMode('light');
        }
      }
    );

    return () => unsubscribe();
  }, []);

  // Determine the actual theme to apply
  const actualTheme = useMemo(() => {
    if (themeMode === 'auto') {
      return systemTheme;
    }
    return themeMode;
  }, [themeMode, systemTheme]);

  // Apply theme to document
  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.documentElement.setAttribute('data-theme', actualTheme);
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [actualTheme]);

  const value = useMemo(
    () => ({
      mode: themeMode,
      theme: actualTheme,
      systemTheme,
    }),
    [themeMode, actualTheme, systemTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
